import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useVendorCategories } from "@/lib/vendorCategories";

// The 5-tile signup picker. Every tile writes a `signup_intent` to the
// backend so we know the WHY behind a signup; role is either kept as the
// picker choice (vendor/organizer) or defaulted to vendor for
// volunteer/donor supporter accounts.
const INTENT_TILES = [
  {
    key: "vendor",
    overline: "Vendor · Free",
    headline: "I'm a maker",
    blurb: "I want to sell at events",
    color: "var(--avt-teal)",
  },
  {
    key: "organizer",
    overline: "Organizer · Free",
    headline: "I host events",
    blurb: "I want to find vendors",
    color: "var(--avt-purple)",
  },
  {
    key: "nonprofit",
    overline: "Nonprofit · 50% off",
    headline: "We're a 501(c)(3)",
    blurb: "Verified badge + priority discovery",
    color: "var(--avt-orange)",
  },
  {
    key: "volunteer",
    overline: "Volunteer · Free",
    headline: "I want to help",
    blurb: "Give my time to nearby causes",
    color: "var(--avt-lime)",
  },
  {
    key: "donor",
    overline: "Donor · Free",
    headline: "I want to give",
    blurb: "Support nonprofits I care about",
    color: "var(--avt-pink)",
  },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `intent` is what the user picked on the 5-tile picker (drives copy +
  // post-signup routing). `role` is the underlying DB role (vendor|organizer)
  // — nonprofit/volunteer/donor collapse to one of those two on the backend.
  const [intent, setIntent] = useState("vendor");
  const [role, setRole] = useState("vendor");
  const [form, setForm] = useState({ email: "", password: "", name: "", business_name: "", bio: "", promo_code: "", referral_code: "" });
  const [terms, setTerms] = useState(false);
  const [optIn, setOptIn] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [promoState, setPromoState] = useState({ status: "idle", info: null }); // idle|checking|valid|invalid
  const [referralState, setReferralState] = useState({ status: "idle", info: null });
  // Vendor categories the new maker picks at signup. 2-3 from the fixed
  // enum (see lib/vendorCategories.js). Only shown/required when the
  // signup intent is "vendor" — volunteers/donors/organizers/nonprofits
  // never need booth categories.
  const [categories, setCategories] = useState([]);
  const cats = useVendorCategories();
  const needsCategories = intent === "vendor";

  const toggleCategory = (value) => {
    setCategories((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= (cats.max || 3)) return prev;
      return [...prev, value];
    });
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const checkPromo = async () => {
    const code = (form.promo_code || "").trim();
    if (!code) { setPromoState({ status: "idle", info: null }); return; }
    setPromoState({ status: "checking", info: null });
    try {
      const { data } = await (await import("@/lib/api")).default.post("/promo-codes/validate", { code });
      if (data?.valid) setPromoState({ status: "valid", info: data });
      else setPromoState({ status: "invalid", info: null });
    } catch {
      setPromoState({ status: "invalid", info: null });
    }
  };

  const checkReferral = async (raw) => {
    const code = (raw ?? form.referral_code ?? "").trim().toUpperCase();
    if (!code) { setReferralState({ status: "idle", info: null }); return; }
    setReferralState({ status: "checking", info: null });
    try {
      const api = (await import("@/lib/api")).default;
      const { data } = await api.get(`/referrals/validate/${encodeURIComponent(code)}`);
      if (data?.valid) setReferralState({ status: "valid", info: data });
      else setReferralState({ status: "invalid", info: null });
    } catch {
      setReferralState({ status: "invalid", info: null });
    }
  };

  // Autofill referral code from ?ref= query param (shareable links) and validate it.
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      const clean = ref.trim().toUpperCase();
      setForm((f) => ({ ...f, referral_code: clean }));
      checkReferral(clean);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Which intent tile the user picks drives the underlying `role` sent to
  // the backend (only 'vendor' and 'organizer' exist as real DB roles).
  // Nonprofit needs a sub-choice; volunteer + donor default to vendor
  // because they mostly consume the platform (browsing + support actions).
  const pickIntent = (next) => {
    setIntent(next);
    if (next === "vendor" || next === "volunteer" || next === "donor") setRole("vendor");
    else if (next === "organizer") setRole("organizer");
    // For 'nonprofit', we leave whatever role was previously picked so the
    // user explicitly selects vendor/organizer via the sub-picker below.
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!terms) { setError("Please accept the Terms and Privacy Policy to continue."); return; }
    if (needsCategories) {
      const min = cats.min || 2;
      const max = cats.max || 3;
      if (categories.length < min || categories.length > max) {
        setError(`Please pick ${min}-${max} categories that describe what you offer.`);
        return;
      }
    }
    setError(""); setBusy(true);
    const promo = (form.promo_code || "").trim();
    const referral = (form.referral_code || "").trim().toUpperCase();
    const res = await register({
      ...form,
      promo_code: promo || undefined,
      referral_code: referral || undefined,
      role,
      signup_intent: intent,
      terms_accepted: true,
      email_opt_in: optIn,
      categories: needsCategories ? categories : undefined,
    });
    setBusy(false);
    if (!res.ok) { setError(res.error); return; }
    toast.success("Account created");
    // Route by signup intent:
    //   nonprofit → /verify-nonprofit (get the 50%-off + verified badge)
    //   volunteer → /volunteer/dashboard (their impact tracker)
    //   donor     → /donor/dashboard (giving history)
    //   vendor / organizer → their dashboard (unchanged)
    if (intent === "nonprofit") navigate("/verify-nonprofit");
    else if (intent === "volunteer") navigate("/volunteer/dashboard");
    else if (intent === "donor") navigate("/donor/dashboard");
    else navigate(res.user.role === "organizer" ? "/organizer/dashboard" : "/vendor/dashboard");
  };

  return (
    <div
      className="min-h-[calc(100vh-88px)] px-6 py-14"
      style={{
        background:
          "radial-gradient(1200px 500px at 15% -10%, rgba(31,93,52,0.08), transparent 65%)," +
          "radial-gradient(900px 500px at 90% 110%, rgba(201,162,75,0.10), transparent 60%)," +
          "var(--bone)",
      }}
    >
      <div className="mx-auto max-w-2xl">
        <Link to="/" data-testid="register-brand-link" className="block text-center mb-6">
          <img
            src="/brand/addvendtour-thumbnail.png"
            alt="AddVendTour"
            className="h-32 w-32 sm:h-36 sm:w-36 mx-auto object-contain drop-shadow-lg rounded-3xl"
          />
          <div className="mt-3 text-sm" style={{ color: "var(--avt-purple)" }}>
            <span style={{ color: "var(--avt-teal)" }}>Add your event.</span>{" "}
            <span style={{ color: "var(--avt-pink)" }}>Book vendors.</span>{" "}
            <span style={{ color: "var(--avt-lime)" }}>Discover more.</span>
          </div>
        </Link>

        <div
          className="rounded-2xl border p-8 sm:p-10"
          style={{
            backgroundColor: "#fff",
            borderColor: "rgba(0,0,0,0.06)",
            boxShadow: "0 30px 80px -40px rgba(21,65,35,0.35), 0 6px 24px -8px rgba(0,0,0,0.06)",
          }}
        >
          <div className="overline" style={{ color: "var(--terracotta)" }}>Join the market</div>
          <h1 className="font-serif text-4xl mt-2">Create your account.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--ink-soft)" }}>
            Already have one? <Link to="/login" data-testid="register-to-login" className="underline">Log in</Link>
          </p>

          <div className="mt-8">
            <div className="overline mb-3" style={{ color: "var(--ink-soft)" }}>I&apos;m joining as a…</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="intent-grid">
              {INTENT_TILES.map((t) => {
                const active = intent === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    data-testid={`intent-${t.key}`}
                    onClick={() => pickIntent(t.key)}
                    className={`p-4 text-left rounded-xl border-2 transition ${active ? "bg-white shadow-md" : "bg-transparent"}`}
                    style={{ borderColor: active ? t.color : "var(--border-warm)" }}
                  >
                    <div className="overline" style={{ color: active ? t.color : "var(--ink-soft)" }}>{t.overline}</div>
                    <div className="font-serif text-xl mt-1.5" style={{ color: active ? t.color : "var(--ink)" }}>{t.headline}</div>
                    <div className="text-xs mt-2" style={{ color: "var(--ink-soft)" }}>{t.blurb}</div>
                  </button>
                );
              })}
            </div>

            {intent === "nonprofit" && (
              <div
                data-testid="nonprofit-subpicker"
                className="mt-4 rounded-xl border-2 p-4"
                style={{
                  backgroundColor: "rgba(249,115,22,0.06)",
                  borderColor: "rgba(249,115,22,0.35)",
                }}
              >
                <div className="text-sm mb-3" style={{ color: "var(--ink)" }}>
                  How will your nonprofit show up on AddVendTour?
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="nonprofit-as-organizer"
                    onClick={() => setRole("organizer")}
                    className={`p-3 text-left rounded-lg border transition ${role === "organizer" ? "bg-white" : "bg-transparent"}`}
                    style={{ borderColor: role === "organizer" ? "var(--avt-orange)" : "var(--border-warm)" }}
                  >
                    <div className="text-sm font-semibold">Hosting events</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>Fundraisers, community markets, drives</div>
                  </button>
                  <button
                    type="button"
                    data-testid="nonprofit-as-vendor"
                    onClick={() => setRole("vendor")}
                    className={`p-3 text-left rounded-lg border transition ${role === "vendor" ? "bg-white" : "bg-transparent"}`}
                    style={{ borderColor: role === "vendor" ? "var(--avt-orange)" : "var(--border-warm)" }}
                  >
                    <div className="text-sm font-semibold">Selling at events</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-soft)" }}>Merch, crafts, or programs at other markets</div>
                  </button>
                </div>
                <div className="text-xs mt-3" style={{ color: "var(--avt-orange)" }}>
                  You&apos;ll get a verified badge + 50% off Pro after we confirm your 501(c)(3).
                </div>
              </div>
            )}

            {(intent === "volunteer" || intent === "donor") && (
              <div
                data-testid={`${intent}-note`}
                className="mt-4 rounded-xl border-2 p-4"
                style={{
                  backgroundColor: intent === "volunteer" ? "rgba(132,204,22,0.08)" : "rgba(236,72,153,0.06)",
                  borderColor: intent === "volunteer" ? "rgba(132,204,22,0.4)" : "rgba(236,72,153,0.35)",
                }}
              >
                <div className="text-sm" style={{ color: "var(--ink)" }}>
                  {intent === "volunteer"
                    ? "Awesome — creating your account lets us remember your history and send you nearby volunteer opportunities. You can also just skip signup and click Volunteer on any event."
                    : "Thank you — creating your account keeps your donation history and receipts in one place. You can also just skip signup and donate on any event page."}
                </div>
              </div>
            )}
          </div>

      <form onSubmit={onSubmit} className="mt-8 space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-widest">Full name</Label>
          <Input data-testid="register-name" value={form.name} onChange={upd("name")} required className="mt-2" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-widest">Email</Label>
            <Input data-testid="register-email" type="email" value={form.email} onChange={upd("email")} required className="mt-2" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Password</Label>
            <Input data-testid="register-password" type="password" value={form.password} onChange={upd("password")} required minLength={6} className="mt-2" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">
            {intent === "nonprofit" ? "Nonprofit name"
              : intent === "organizer" ? "Organization name"
              : intent === "volunteer" ? "Display name (optional)"
              : intent === "donor" ? "Display name (optional)"
              : "Brand / Studio name"}
          </Label>
          <Input data-testid="register-business" value={form.business_name} onChange={upd("business_name")} className="mt-2" />
        </div>
        {needsCategories && (
          <div data-testid="register-categories-block">
            <Label className="text-xs uppercase tracking-widest">
              What do you offer?{" "}
              <span
                className="lowercase font-normal"
                style={{ color: "var(--ink-soft)" }}
              >
                (pick {cats.min || 2}-{cats.max || 3} — organizers search on these)
              </span>
            </Label>
            <div
              data-testid="register-categories"
              className="mt-2 flex flex-wrap gap-2"
            >
              {(cats.categories || []).map((c) => {
                const active = categories.includes(c.value);
                const full = categories.length >= (cats.max || 3);
                return (
                  <button
                    key={c.value}
                    type="button"
                    data-testid={`register-category-${c.value}`}
                    aria-pressed={active}
                    onClick={() => toggleCategory(c.value)}
                    disabled={!active && full}
                    className="px-3 py-2 rounded-full border-2 text-sm transition"
                    style={{
                      borderColor: active
                        ? "var(--terracotta)"
                        : "var(--border-warm)",
                      backgroundColor: active
                        ? "rgba(201,67,42,0.10)"
                        : "transparent",
                      color: active ? "var(--terracotta)" : "var(--ink)",
                      opacity: !active && full ? 0.45 : 1,
                      cursor: !active && full ? "not-allowed" : "pointer",
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div
              className="mt-1.5 text-[11px]"
              style={{ color: "var(--ink-soft)" }}
              data-testid="register-categories-count"
            >
              {categories.length} of {cats.max || 3} selected
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-widest">
            Referral code <span className="lowercase font-normal" style={{ color: "var(--ink-soft)" }}>(from a friend — get your first month of Maker Pro free)</span>
          </Label>
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="register-referral-code"
              value={form.referral_code}
              onChange={(e) => { setForm({ ...form, referral_code: e.target.value.toUpperCase() }); setReferralState({ status: "idle", info: null }); }}
              onBlur={() => checkReferral()}
              placeholder="MS-XXXXXX"
              className="uppercase"
            />
            <Button
              type="button"
              data-testid="register-referral-check"
              variant="outline"
              onClick={() => checkReferral()}
              disabled={!form.referral_code || referralState.status === "checking"}
              className="rounded-full whitespace-nowrap"
            >
              {referralState.status === "checking" ? "Checking…" : "Apply"}
            </Button>
          </div>
          {referralState.status === "valid" && referralState.info && (
            <div
              data-testid="register-referral-valid"
              className="mt-2 text-xs rounded-sm p-2.5 border"
              style={{
                backgroundColor: "rgba(31,93,52,0.08)",
                borderColor: "rgba(31,93,52,0.3)",
                color: "var(--forest)",
              }}
            >
              ✓ You were invited by <strong>{referralState.info.referrer_name}</strong>. Your first month of Maker Pro will be <strong>free</strong> when you upgrade.
            </div>
          )}
          {referralState.status === "invalid" && (
            <div
              data-testid="register-referral-invalid"
              className="mt-2 text-xs"
              style={{ color: "var(--terracotta)" }}
            >
              We couldn’t find that referral code. Double-check it or ask your friend for the exact link.
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Promo code <span className="lowercase font-normal" style={{ color: "var(--ink-soft)" }}>(optional)</span></Label>
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="register-promo-code"
              value={form.promo_code}
              onChange={(e) => { upd("promo_code")(e); setPromoState({ status: "idle", info: null }); }}
              onBlur={checkPromo}
              placeholder="LAUNCH50"
              className="uppercase"
            />
            <Button
              type="button"
              data-testid="register-promo-check"
              variant="outline"
              onClick={checkPromo}
              disabled={!form.promo_code || promoState.status === "checking"}
              className="rounded-full whitespace-nowrap"
            >
              {promoState.status === "checking" ? "Checking…" : "Apply"}
            </Button>
          </div>
          {promoState.status === "valid" && promoState.info && (
            <div
              data-testid="register-promo-valid"
              className="mt-2 text-xs rounded-sm p-2.5 border"
              style={{
                backgroundColor: "rgba(111,125,85,0.08)",
                borderColor: "rgba(111,125,85,0.3)",
                color: "var(--moss)",
              }}
            >
              ✓ <strong>{promoState.info.percent_off}% off</strong> Maker Pro (first billing cycle). We’ll auto-apply this when you upgrade.
            </div>
          )}
          {promoState.status === "invalid" && (
            <div
              data-testid="register-promo-invalid"
              className="mt-2 text-xs"
              style={{ color: "var(--terracotta)" }}
            >
              We couldn’t find that code. It may be expired or used up.
            </div>
          )}
        </div>

        {error && <div data-testid="register-error" className="text-sm" style={{ color: "var(--terracotta)" }}>{error}</div>}

        <div className="flex items-start gap-3">
          <Checkbox id="terms" data-testid="register-terms" checked={terms} onCheckedChange={(v) => setTerms(Boolean(v))} className="mt-1" />
          <label htmlFor="terms" className="text-sm cursor-pointer" style={{ color: "var(--ink-soft)" }}>
            I agree to the{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid="register-terms-link"
              className="underline"
            >Terms of Service</a>
            {" "}and{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid="register-privacy-link"
              className="underline"
            >Privacy Policy</a>.
          </label>
        </div>

        <div className="flex items-start gap-3">
          <Checkbox id="optin" data-testid="register-optin" checked={optIn} onCheckedChange={(v) => setOptIn(Boolean(v))} className="mt-1" />
          <label htmlFor="optin" className="text-sm cursor-pointer" style={{ color: "var(--ink-soft)" }}>
            Send me event opportunities in my city — a curated weekly digest. Unsubscribe anytime.
          </label>
        </div>

        <Button type="submit" data-testid="register-submit" disabled={busy} className="btn-primary w-full py-6 rounded-full">
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-xs text-center" style={{ color: "var(--ink-soft)" }}>
          Free forever. {
            intent === "vendor" ? "Every new maker gets 7 days of Maker Pro on us — no card required."
            : intent === "organizer" ? "Every new organizer gets 7 days of Organizer Pro on us — full AI planning suite, no card required."
            : intent === "nonprofit" ? "We'll verify your 501(c)(3) and unlock 50% off Pro + a verified badge."
            : intent === "volunteer" ? "We'll help you find volunteer opportunities in your city."
            : "We'll help you discover nonprofits and events worth supporting."
          }
        </p>
      </form>
        </div>
      </div>
    </div>
  );
}
